import { createStyles, Group, Modal, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { Button, Icon, Text } from '@repo/foundations';
import { lowerCase } from 'lodash-es';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import type { ITemplate } from '../../api';
import { ENTITY_TYPES_MODAL_CONFIG } from '../../components/EntityModal/constants';
import { AssigneeSelector } from '../../components/EntityModal/Metadata/AssigneeSelector';
import CollectionSelector from '../../components/EntityModal/Metadata/CollectionSelector';
import { OwnerSelector } from '../../components/EntityModal/Metadata/OwnerSelector';
import { ParentCollectionSelector } from '../../components/EntityModal/Metadata/ParentCollectionSelector';
import { PrioritySelector } from '../../components/EntityModal/Metadata/PrioritySelector';
import { TeamSelector } from '../../components/EntityModal/Metadata/TeamSelector';
import { TemplateSelector } from '../../components/EntityModal/Metadata/TemplateSelector';
import { openConfirmModal } from '../../components/ModalManager/events';
import TagPropertySelector from '../../components/Properties/TagPropertySelector';
import { usePreventNavigation } from '../../hooks/usePreventNavigation';
import { AiChatEmbedPreviewController } from '../AiChatEmbedPreview';
import { ContentInput } from './components/ContentInput';
import { RelatedQuestions } from './components/RelatedQuestions';
import { TitleInput } from './components/TitleInput';
import { useCreateEntity } from './hooks/useCreateEntity';
import { createEntityModalStore } from './store';

const useStyles = createStyles((theme) => ({
	body: {
		padding: `0 !important`,
	},
	footer: {
		position: 'sticky',
		bottom: 0,
		backgroundColor: theme.other.getColor('fill/primary/default'),
		borderTop: `1px solid ${theme.other.getColor('border/primary/default')}`,
	},
}));

export const CreateEntityModalController = observer(() => {
	const { classes } = useStyles();

	const handleTeamChange = useCallback((value: string) => {
		runInAction(() => {
			createEntityModalStore.setTeams([value]);
		});
	}, []);

	const handleTemplateChange = useCallback(async (value: ITemplate) => {
		runInAction(async () => {
			await createEntityModalStore.setTemplate(value, false);
			createEntityModalStore.triggerEditorRender();
		});
	}, []);

	const handleTitleChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			runInAction(() => {
				createEntityModalStore.setTitle(e.target.value);
			});
		},
		[]
	);

	const handleDescriptionChange = useCallback((value: string | undefined) => {
		runInAction(() => {
			createEntityModalStore.setDescription(value ?? '');
		});
	}, []);

	const handleChange = useCallback(
		(key: string) => (value: string | string[]) => {
			runInAction(() => {
				createEntityModalStore.setMetadata(key, value);
			});
		},
		[]
	);

	const handleClose = useCallback(() => {
		if (createEntityModalStore.hasUnsavedChanges) {
			openConfirmModal({
				title: 'You have unsaved changes',
				children: (
					<Text size="sm">
						You'll lose your changes if you close this dialog. Are you sure you
						wish to close?
					</Text>
				),
				labels: { confirm: 'Confirm', cancel: 'Cancel' },
				onConfirm: () => createEntityModalStore.reset(),
			});
		} else {
			createEntityModalStore.reset();
		}
	}, []);

	usePreventNavigation(createEntityModalStore.hasUnsavedChanges);

	const createEntity = useCreateEntity();

	const handleSubmit = useCallback(() => {
		createEntityModalStore.createEntity(async (args) => {
			try {
				await createEntity(args);
				createEntityModalStore.reset();
			} catch (error) {
				showNotification({
					title: `Error creating ${args.entityType}`,
					message: 'If the issue persists, please contact support',
					color: 'red',
					icon: (
						<Icon name="alertCircle" color="text/critical-on-fill/default" />
					),
				});
				throw error;
			}
		});
	}, [createEntity]);

	if (!createEntityModalStore.opened) {
		return null;
	}

	const withTemplate =
		createEntityModalStore.entityType &&
		ENTITY_TYPES_MODAL_CONFIG[createEntityModalStore.entityType].withTemplate;

	const config =
		createEntityModalStore.entityType &&
		ENTITY_TYPES_MODAL_CONFIG[createEntityModalStore.entityType];

	return (
		<Modal.Root opened centered size="xl" onClose={handleClose}>
			<Modal.Overlay />
			<Modal.Content>
				<Modal.Header>
					<Group spacing="xs">
						<TeamSelector onChange={handleTeamChange} />
						{withTemplate && (
							<TemplateSelector
								onChange={handleTemplateChange}
								entityType={createEntityModalStore.entityType!}
								initialValue={createEntityModalStore.template?.id}
							/>
						)}
					</Group>
					<Modal.CloseButton />
				</Modal.Header>
				<Modal.Body className={classes.body}>
					<Stack spacing="xl" p="xl">
						<Stack spacing="md">
							<TitleInput
								value={createEntityModalStore.title}
								onChange={handleTitleChange}
								error={createEntityModalStore.titleValidationError}
							/>
							<ContentInput
								key={createEntityModalStore.editorRenderKey}
								initialValue={createEntityModalStore.description}
								onChangeCallback={handleDescriptionChange}
								emptyDocPlaceholder={config?.richEditorPlaceholder}
								{...(config?.richEditorProps ?? {})}
							/>
						</Stack>
						{createEntityModalStore.attachment?.type === 'ai_chat_message' && (
							<AiChatEmbedPreviewController
								promptId={createEntityModalStore.attachment.attachmentId}
								onRemove={createEntityModalStore.removeAttachment}
							/>
						)}
						{createEntityModalStore.relatedQuestions?.length && (
							<RelatedQuestions
								relatedQuestions={createEntityModalStore.relatedQuestions}
								onClickQuestion={createEntityModalStore.reset}
							/>
						)}
						<Group spacing="xs">
							{config?.withOwners && (
								<OwnerSelector
									initialValue={
										createEntityModalStore.metadata.owners as
											| string[]
											| undefined
									}
									onChangeUserOwners={handleChange('owners')}
								/>
							)}
							{config?.withParentCollection && (
								<ParentCollectionSelector
									initialValue={
										createEntityModalStore.metadata.parent as string | undefined
									}
									onChange={handleChange('parent')}
								/>
							)}
							{config?.withCollections && (
								<CollectionSelector
									initialValue={
										createEntityModalStore.metadata.collections as
											| string[]
											| undefined
									}
									placeholderIconName="folders"
									onChange={handleChange('collections')}
								/>
							)}
							{config?.withAssignee && (
								<AssigneeSelector
									initialValue={
										createEntityModalStore.metadata.assignee as
											| string
											| undefined
									}
									onChange={handleChange}
								/>
							)}
							{config?.withPriority && (
								<PrioritySelector
									initialValue={
										createEntityModalStore.metadata.priority as
											| string
											| undefined
									}
									onChange={handleChange('priority')}
								/>
							)}
							{config?.withTags && (
								<TagPropertySelector
									initialValue={
										createEntityModalStore.metadata.tags as string[] | undefined
									}
									placeholderIconName="tag"
									placeholder="Add tags"
									onChange={handleChange('tags')}
								/>
							)}
						</Group>
					</Stack>
					<Group
						spacing="xs"
						p="md"
						position="right"
						className={classes.footer}
					>
						<Button variant="default" onClick={handleClose}>
							Cancel
						</Button>
						<Button
							variant="primary"
							loading={createEntityModalStore.loading}
							disabled={createEntityModalStore.loading}
							onClick={handleSubmit}
						>
							Create {lowerCase(createEntityModalStore.entityType)}
						</Button>
					</Group>
				</Modal.Body>
			</Modal.Content>
		</Modal.Root>
	);
});
